// Shared pieces for the paged lists (People, Family groups, Recipes). Filter, search and
// page live in the address bar, so back/forward and shared links work.
import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { formatNumber } from './ui'

export const PAGE_SIZE = 25

export const useListParams = (defaults = {}) => {
  const [params, setParams] = useSearchParams()
  const read = (key) => params.get(key) ?? defaults[key] ?? ''
  const page = Math.max(1, parseInt(params.get('page') || '1', 10) || 1)

  // Changing anything but the page goes back to page 1
  const update = (changes, { replace = false } = {}) => {
    const next = new URLSearchParams(params)
    Object.entries(changes).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined || value === defaults[key]) {
        next.delete(key)
      } else {
        next.set(key, String(value))
      }
    })
    if (!('page' in changes)) next.delete('page')
    if (next.get('page') === '1') next.delete('page')
    setParams(next, { replace })
  }

  return { read, page, update, key: params.toString() }
}

// A search box for one list; waits a moment after typing before searching
export const ListSearch = ({ value, onChange, placeholder }) => {
  const [text, setText] = useState(value)

  useEffect(() => {
    setText(value)
  }, [value])

  useEffect(() => {
    if (text.trim() === value.trim()) return undefined
    const timer = setTimeout(() => onChange(text.trim()), 300)
    return () => clearTimeout(timer)
  }, [text, value, onChange])

  return (
    <input
      type="search"
      className="a-filter-input"
      value={text}
      placeholder={placeholder}
      aria-label={placeholder}
      onChange={(e) => setText(e.target.value)}
    />
  )
}

export const Chips = ({ options, value, counts = {}, onChange, label = 'Show' }) => (
  <div className="a-toolbar" role="group" aria-label={label}>
    {options.map(([key, text]) => (
      <button
        type="button"
        key={key}
        className="a-chip"
        aria-pressed={value === key}
        onClick={() => onChange(key)}
      >
        {text}
        {counts[key] !== undefined && counts[key] !== null ? (
          <span className="a-n">{formatNumber(counts[key])}</span>
        ) : null}
      </button>
    ))}
  </div>
)

export const Pager = ({ data, page, onPage, note = 'newest first' }) => {
  const total = Number(data?.total) || 0
  const perPage = Number(data?.perPage) || PAGE_SIZE
  const lastPage = Number(data?.lastPage) || Math.max(1, Math.ceil(total / perPage))
  const shown = data?.data?.length || 0
  const from = total ? (page - 1) * perPage + 1 : 0
  const to = total ? from + shown - 1 : 0
  return (
    <div className="a-pager">
      <span>
        {total
          ? `Showing ${formatNumber(from)}–${formatNumber(to)} of ${formatNumber(total)}`
          : 'Nothing to show'}
        {note ? ` · ${note}` : ''}
      </span>
      <div className="a-btn-row">
        <button
          type="button"
          className="a-btn a-small"
          disabled={page <= 1}
          aria-disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Previous
        </button>
        <button
          type="button"
          className="a-btn a-small"
          disabled={page >= lastPage}
          aria-disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  )
}
